import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, Transition } from '@headlessui/react';
import {
  MagnifyingGlassIcon,
  HomeIcon,
  UsersIcon,
  CubeIcon,
  DocumentTextIcon,
  WalletIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  PlusCircleIcon,
  SparklesIcon,
} from '@heroicons/react/24/outline';
import { clientService, documentService, articleService } from '../../api/services';

interface CommandItem {
  id: string;
  group: string;
  label: string;
  hint?: string;
  icon: React.FC<{ className?: string }>;
  onSelect: () => void;
  keywords?: string[];
}

interface Props {
  open: boolean;
  onClose: () => void;
  onOpenAI?: () => void;
}

export function CommandPalette({ open, onClose, onOpenAI }: Props) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [dynamic, setDynamic] = useState<CommandItem[]>([]);
  const [active, setActive] = useState(0);

  // Reset query when opening
  useEffect(() => {
    if (open) {
      setQuery('');
      setActive(0);
    }
  }, [open]);

  // Load fresh suggestions when the palette opens — recent clients, latest docs,
  // and popular articles. Failures are swallowed so the palette stays usable even
  // when the backend hiccups.
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [clients, docs, articles] = await Promise.all([
          clientService.getAll({ size: 5 }).then((p) => p.content).catch(() => []),
          documentService.getAll({ size: 5 }).then((p) => p.content).catch(() => []),
          articleService.getAll({ size: 5 }).then((p) => p.content).catch(() => []),
        ]);
        const items: CommandItem[] = [
          ...clients.map((c: any) => ({
            id: `client-${c.id}`,
            group: 'Clients',
            label: c.raisonSociale,
            hint: c.matriculeFiscal || c.code || '',
            icon: UsersIcon,
            onSelect: () => navigate(`/clients/${c.id}/edit`),
            keywords: [c.raisonSociale, c.code, c.matriculeFiscal].filter(Boolean),
          })),
          ...docs.map((d: any) => ({
            id: `doc-${d.id}`,
            group: 'Documents',
            label: `${d.numero} — ${d.clientRaisonSociale || ''}`,
            hint: d.typeDocument,
            icon: DocumentTextIcon,
            onSelect: () => navigate(`/documents/view/${d.id}`),
            keywords: [d.numero, d.clientRaisonSociale].filter(Boolean),
          })),
          ...articles.map((a: any) => ({
            id: `article-${a.id}`,
            group: 'Articles',
            label: a.designation,
            hint: a.code || a.reference,
            icon: CubeIcon,
            onSelect: () => navigate(`/articles/${a.id}/edit`),
            keywords: [a.designation, a.code, a.reference].filter(Boolean),
          })),
        ];
        setDynamic(items);
      } catch {
        setDynamic([]);
      }
    })();
  }, [open, navigate]);

  const staticItems: CommandItem[] = useMemo(() => [
    { id: 'nav-dashboard', group: 'Navigation', label: 'Tableau de bord', icon: HomeIcon, onSelect: () => navigate('/') },
    { id: 'nav-clients', group: 'Navigation', label: 'Clients', icon: UsersIcon, onSelect: () => navigate('/clients') },
    { id: 'nav-articles', group: 'Navigation', label: 'Articles', icon: CubeIcon, onSelect: () => navigate('/articles') },
    { id: 'nav-factures', group: 'Navigation', label: 'Factures', icon: DocumentTextIcon, onSelect: () => navigate('/documents/factures') },
    { id: 'nav-devis', group: 'Navigation', label: 'Devis', icon: DocumentTextIcon, onSelect: () => navigate('/documents/devis') },
    { id: 'nav-paiements', group: 'Navigation', label: 'Paiements', icon: WalletIcon, onSelect: () => navigate('/paiements') },
    { id: 'nav-tva', group: 'Navigation', label: 'Déclaration TVA', icon: ChartBarIcon, onSelect: () => navigate('/rapports/tva') },
    { id: 'nav-etat', group: 'Navigation', label: 'État annuel', icon: ChartBarIcon, onSelect: () => navigate('/rapports/etat-annuel') },
    { id: 'nav-export', group: 'Navigation', label: 'Export comptable', icon: ChartBarIcon, onSelect: () => navigate('/rapports/export') },
    { id: 'nav-params', group: 'Navigation', label: 'Paramètres', icon: Cog6ToothIcon, onSelect: () => navigate('/parametres') },

    { id: 'act-new-invoice', group: 'Actions', label: 'Nouvelle facture', icon: PlusCircleIcon, onSelect: () => navigate('/documents/new?type=FACTURE') },
    { id: 'act-new-devis', group: 'Actions', label: 'Nouveau devis', icon: PlusCircleIcon, onSelect: () => navigate('/documents/new?type=DEVIS') },
    { id: 'act-new-client', group: 'Actions', label: 'Nouveau client', icon: PlusCircleIcon, onSelect: () => navigate('/clients/new') },
    { id: 'act-new-article', group: 'Actions', label: 'Nouvel article', icon: PlusCircleIcon, onSelect: () => navigate('/articles/new') },

    ...(onOpenAI ? [{
      id: 'ai-open', group: 'IA', label: "Ouvrir l'assistant IA",
      hint: 'Posez une question métier', icon: SparklesIcon, onSelect: () => onOpenAI(),
    }] : []),
  ], [navigate, onOpenAI]);

  const all = useMemo(() => [...staticItems, ...dynamic], [staticItems, dynamic]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return all;
    return all.filter((it) => {
      const hay = [it.label, it.hint || '', it.group, ...(it.keywords || [])]
        .join(' ').toLowerCase();
      return q.split(/\s+/).every((tok) => hay.includes(tok));
    });
  }, [all, query]);

  const grouped = useMemo(() => {
    const map = new Map<string, CommandItem[]>();
    for (const it of filtered) {
      if (!map.has(it.group)) map.set(it.group, []);
      map.get(it.group)!.push(it);
    }
    return Array.from(map.entries());
  }, [filtered]);

  // Track the active item so arrow keys can drive selection.
  useEffect(() => {
    setActive(0);
  }, [query]);

  const select = (it: CommandItem) => {
    it.onSelect();
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(filtered.length - 1, a + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(0, a - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (filtered[active]) select(filtered[active]);
    }
  };

  return (
    <Transition show={open} as={React.Fragment} appear>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-150" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/30 backdrop-blur-sm" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-start justify-center pt-[12vh] px-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-150" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100"
            leave="ease-in duration-100" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-gray-200 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-100">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
                <input
                  autoFocus
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={onKeyDown}
                  placeholder="Rechercher, créer, naviguer…"
                  className="flex-1 bg-transparent outline-none text-sm placeholder:text-gray-400"
                />
                <kbd className="hidden sm:inline-block text-[10px] font-mono text-gray-400 bg-gray-100 rounded px-1.5 py-0.5">ESC</kbd>
              </div>
              <div className="max-h-[60vh] overflow-y-auto py-2">
                {filtered.length === 0 ? (
                  <div className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucun résultat pour « {query} »
                  </div>
                ) : (
                  grouped.map(([group, items]) => (
                    <div key={group} className="mb-2 last:mb-0">
                      <div className="px-4 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                        {group}
                      </div>
                      {items.map((it) => {
                        const idx = filtered.indexOf(it);
                        const isActive = idx === active;
                        const Icon = it.icon;
                        return (
                          <button
                            key={it.id}
                            type="button"
                            onMouseEnter={() => setActive(idx)}
                            onClick={() => select(it)}
                            className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm ${
                              isActive ? 'bg-emerald-50 text-emerald-900' : 'text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            <Icon className="h-4 w-4 text-gray-500" />
                            <span className="flex-1 truncate">{it.label}</span>
                            {it.hint && (
                              <span className="text-xs text-gray-400 truncate max-w-[40%]">{it.hint}</span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
              <div className="flex items-center justify-between px-4 py-2 border-t border-gray-100 bg-gray-50 text-[11px] text-gray-500">
                <span>
                  <kbd className="font-mono bg-white border border-gray-200 rounded px-1">↑↓</kbd> naviguer
                  &nbsp;·&nbsp;
                  <kbd className="font-mono bg-white border border-gray-200 rounded px-1">↵</kbd> ouvrir
                </span>
                <span>{filtered.length} résultat{filtered.length > 1 ? 's' : ''}</span>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}

export default CommandPalette;
